import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/feature/ArticleCard";
import { useSeo } from "@/hooks/useSeo";
import { articles, categories, hotRank, topics } from "@/mocks/content";

export default function TopicPage() {
  const { slug } = useParams();
  const category = categories.find((item) => item.slug === slug);
  const topic = topics.find((item) => item.slug === slug);
  const list = articles.filter((article) => article.category === slug);

  useSeo({
    title: category
      ? `${category.name}专题_最新爆料合集 - 吃瓜日报`
      : "专题未找到 - 吃瓜日报",
    description: category
      ? `${category.name}专题：${category.desc}。吃瓜日报整理该分类下的今日瓜与最新爆料，含事件时间线与当事方回应，帮你在五分钟内看清一个瓜的来龙去脉。`
      : "该专题可能已调整，请返回今日瓜列表查看最新爆料。",
    keywords: category
      ? `${category.name},网红吃瓜,今日瓜,最新爆料,新鲜瓜`
      : "网红吃瓜,今日瓜",
    canonical: category ? `https://jcspower.cn/topic/${category.slug}` : undefined,
    jsonLd: category
      ? {
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: `${category.name}专题`,
          description: category.desc,
          url: `https://jcspower.cn/topic/${category.slug}`,
        }
      : undefined,
  });

  if (!category) {
    return (
      <SiteLayout>
        <div className="mx-auto w-full px-4 py-20 text-center md:px-6">
          <h1 className="font-heading text-xl font-bold text-foreground-950">这个专题还没有内容</h1>
          <p className="mt-2 text-sm text-foreground-600">返回列表看看今天的其他瓜。</p>
          <Link
            to="/news"
            className="mt-5 inline-flex whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors duration-200 hover:bg-primary-600"
          >
            返回今日瓜列表
          </Link>
        </div>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/50">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs
            items={[
              { label: "今日瓜列表", to: "/news" },
              { label: category.name },
            ]}
          />
          <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="flex items-center gap-2 text-xs font-medium tracking-[0.2em] text-primary-600">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-folder-open-line text-base" aria-hidden="true" />
                </span>
                TOPIC ARCHIVE
              </span>
              <h1 className="mt-3 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                {topic ? topic.title : category.name}
              </h1>
              <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
                {topic ? topic.desc : category.desc}
              </p>
            </div>
            <div className="flex shrink-0 gap-3">
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-lg font-bold text-primary-600">{category.count}</p>
                <p className="mt-0.5 text-xs text-foreground-500">累计瓜料</p>
              </div>
              <div className="rounded-lg border border-background-200/80 bg-background-50 px-5 py-3 text-center">
                <p className="font-heading text-lg font-bold text-accent-600">{list.length}</p>
                <p className="mt-0.5 text-xs text-foreground-500">本次收录</p>
              </div>
            </div>
          </div>

          <div className="no-scrollbar mt-6 flex items-center gap-2 overflow-x-auto pb-1">
            <Link
              to="/news"
              className="whitespace-nowrap rounded-full bg-background-50 px-3.5 py-1.5 text-sm font-medium text-foreground-700 transition-colors duration-200 hover:bg-background-200"
            >
              全部瓜料
            </Link>
            {categories.map((item) => (
              <Link
                key={item.slug}
                to={`/topic/${item.slug}`}
                className={`whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${
                  item.slug === category.slug
                    ? "bg-foreground-950 text-background-50"
                    : "bg-background-50 text-foreground-700 hover:bg-background-200"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-10">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {list.length > 0 ? (
              <div className="flex flex-col gap-3.5">
                {list.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-50 p-10 text-center">
                <p className="text-sm font-medium text-foreground-800">这个分类下的瓜料还在整理中</p>
                <p className="mt-1 text-xs text-foreground-500">先去其他分类围观一下吧</p>
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6 lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">其他专题</h2>
              <ul className="mt-4 flex flex-col">
                {topics
                  .filter((item) => item.slug !== category.slug)
                  .map((item) => (
                    <li key={item.slug} className="border-b border-background-200/70 last:border-0">
                      <Link
                        to={`/topic/${item.slug}`}
                        className="group flex items-center justify-between gap-3 py-3"
                      >
                        <span className="text-sm font-medium text-foreground-900 transition-colors duration-200 group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="flex h-4 w-4 items-center justify-center text-foreground-400 transition-colors duration-200 group-hover:text-primary-500">
                          <i className="ri-arrow-right-s-line text-base" aria-hidden="true" />
                        </span>
                      </Link>
                    </li>
                  ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200/80 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">吃瓜热榜</h2>
              <ol className="mt-4 flex flex-col">
                {hotRank.slice(0, 5).map((item, index) => (
                  <li key={item.id} className="border-b border-background-200/70 last:border-0">
                    <Link to={`/news/${item.id}`} className="group flex items-start gap-3 py-3">
                      <span
                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded font-heading text-xs font-bold ${
                          index < 3 ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-600"
                        }`}
                      >
                        {index + 1}
                      </span>
                      <span className="clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-600">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ol>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}